import { SemesterCoursesContainer } from '@/styles/Containers';
import { CustomizableHeading } from '@/styles/Text';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FlexColumn, FlexContainer } from '@/styles/Containers';
import { Letter, GreyText } from '@/styles/Text';
import { HalfLeftOval, HalfRightOval } from '@/styles/Shapes';
import { SemesterContainer } from '@/styles/Containers';
import CalculatorCourse from './CalculatorCourse';
import { CollapseButton } from '@/styles/Buttons';
import ExpandMore from '../../assets/icons/ExpandMore.svg';
import ExpandLess from '../../assets/icons/ExpandLess.svg';
import InputSearchOval from './InputSearchOval';
import { getLetterGrade } from './CalculateGrade';
import { calculateAverageGrade } from './CalculateGrade';
import useSize from '@/utils/useSize';

const TopRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const TextContainer = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.color.black};
  background-color: ${({ theme }) => theme.color.lightlightBlue};
  border-bottom: none;
  border-left: none;
  border-right: none;
  border-radius: 0;
  :last-child {
    border: none;
  }
`;

type CalculatorSemesterProps = {
  semester: string;
  calculatorCourses: CalculatorCourse[];
  grade: number;
};

type SemesterCalculatorProps = {
  semester: string;
  courses: CalculatorCourse[];
  onGradeChange: (semester: string, newGrade: number, courses: CalculatorCourse[]) => void;
  semesters?: CalculatorSemesterProps[];
  updateTotalCoursePoints: (points: number) => void;
};

export type CalculatorCourse = {
  courseCode: string;
  courseName: string;
  coursePoint: number;
  grade: number;
};

const SemesterCalculator: React.FC<SemesterCalculatorProps> = (props) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [courseVisibility, setCourseVisibility] = useState(props.courses.map(() => true));
  const [semesterVisibility, setSemesterVisible] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [calculatorCourses, setCalculatorCourses] = useState<CalculatorCourse[]>(props.courses);
  const [totalPoints, setTotalPoints] = useState<number>(0);
  const [averageGrade, setAverageGrade] = useState<number>(0);
  const [letterGrade, setLetterGrade] = useState<string | undefined>('');
  const { isMobile, size } = useSize();

  const addCourseToSemester = (course: CalculatorCourse) => {
    setCalculatorCourses((prevCourses) => [...prevCourses, course]);
    setCourseVisibility((prevVisibility) => [...prevVisibility, true]);
  };

  const handleDeleteCourse = (courseCodeToDelete: string) => {
    setCalculatorCourses((prevCourses) => {
      const updatedCourses = prevCourses.filter((course) => course.courseCode !== courseCodeToDelete);
      setCourseVisibility(updatedCourses.map(() => true));
      return updatedCourses;
    });
  };

  const handleAvgGrade = useCallback(() => {
    const averagegrade = calculateAverageGrade(calculatorCourses);
    setAverageGrade(averagegrade);
    setLetterGrade(getLetterGrade(averagegrade));
    props.onGradeChange(props.semester, averagegrade, calculatorCourses);
  }, [calculatorCourses]);

  useEffect(() => {
    let totalPoints = 0;
    calculatorCourses.forEach((course) => {
      totalPoints += course.coursePoint;
    });
    setTotalPoints(totalPoints);
    props.updateTotalCoursePoints(totalPoints);
  }, [calculatorCourses]);

  const handleSelectGrade = (courseCode: string, selectedGrade: number) => {
    setCalculatorCourses((prevList) => {
      return prevList.map((course) => {
        if (course.courseCode === courseCode) {
          return { ...course, grade: selectedGrade };
        } else {
          return course;
        }
      });
    });
  };

  useEffect(() => {
    if (calculatorCourses.length < 1) {
      setIsExpanded(false);
    }
    const averageGrade = calculateAverageGrade(calculatorCourses);
    handleAvgGrade();
    props.onGradeChange(props.semester, averageGrade, calculatorCourses);
  }, [props.semester, totalPoints, calculatorCourses, handleAvgGrade, averageGrade]);

  return (
    <>
      {calculatorCourses.length > 0 && (
        <FlexContainer flexDirection='row' alignItems='center'>
          <SemesterContainer margin='5vh 0'>
            {semesterVisibility && (
              <TopRow>
                <CollapseButton onClick={() => setIsExpanded(!isExpanded)}>
                  <img src={isExpanded ? ExpandLess : ExpandMore} />
                </CollapseButton>
                <CustomizableHeading fontSize='1.5rem' headingLevel='h2' option='bold'>
                  {props.semester}
                </CustomizableHeading>
              </TopRow>
            )}
            {isExpanded && (
              <>
                {!isMobile ? (
                  <FlexContainer justifySelf='start' margin='0 2.5rem'>
                    <CustomizableHeading margin='0 0 0 2rem' fontSize='1rem' option='bold' headingLevel='h2' mobileFontSize='1rem'>
                      Emnekode
                    </CustomizableHeading>
                    <CustomizableHeading margin='0 0 0 5.5rem' fontSize='1rem' option='bold' headingLevel='h3' mobileFontSize='1rem'>
                      Emnenavn
                    </CustomizableHeading>
                    <CustomizableHeading margin='0 0 0 5rem' fontSize='1rem' option='bold' headingLevel='h3' mobileFontSize='1rem'>
                      Karakter
                    </CustomizableHeading>
                  </FlexContainer>
                ) : (
                  <FlexContainer alignItems='flex-start' gap='4rem' margin='0 1rem'>
                    <CustomizableHeading fontSize='1rem' mobileFontSize='1rem' option='bold' headingLevel='h2'>
                      Emnekode
                    </CustomizableHeading>
                    <CustomizableHeading fontSize='1rem' mobileFontSize='1rem' option='bold' headingLevel='h2'>
                      Emnenavn
                    </CustomizableHeading>
                  </FlexContainer>
                )}
                <SemesterCoursesContainer>
                  {calculatorCourses.map((currentCourse, index) => {
                    return (
                      courseVisibility[index] && (
                        <FlexColumn gap='1rem' alignItems='center' padding='0.1rem 2% 0 2%' key={index}>
                          <CalculatorCourse
                            courseCode={currentCourse.courseCode}
                            courseName={currentCourse.courseName}
                            coursePoint={currentCourse.coursePoint}
                            grade={currentCourse.grade}
                            key={currentCourse.courseCode}
                            onDelete={handleDeleteCourse}
                            onSelectedGrade={handleSelectGrade}
                            onGradeChange={handleAvgGrade}
                          />
                        </FlexColumn>
                      )
                    );
                  })}
                  <>
                    {isMobile ? (
                      <TextContainer>
                        <InputSearchOval addCourseToSemester={addCourseToSemester} setInputValue={setInputValue} inputValue={inputValue} />
                        <GreyText textAlign='right' margin='-1.5rem 0.1rem' fontSize='0.8rem'>
                          Total studiepoeng: {totalPoints} stp
                        </GreyText>
                        <FlexContainer flexDirection='row' alignItems='left' margin='0.5rem 0 -1rem 0.5rem'>
                          <Letter textAlign='left' margin='2.4rem 0 0 1rem' fontSize='0.9rem'>
                            Snitt for semester:
                          </Letter>
                          <FlexContainer margin='2rem 4rem 0 1rem' alignItems='right'>
                            <HalfLeftOval>
                              <Letter fontSize='1.5rem' color='white'>
                                {averageGrade}
                              </Letter>
                            </HalfLeftOval>
                            <HalfRightOval>
                              <Letter fontSize='1.5rem' color='white'>
                                {letterGrade}
                              </Letter>
                            </HalfRightOval>
                          </FlexContainer>
                        </FlexContainer>
                      </TextContainer>
                    ) : (
                      <>
                        <GreyText margin='0rem 2rem' fontSize='1rem' textAlign='right'>
                          Total studiepoeng: {totalPoints} stp
                        </GreyText>
                        <FlexContainer alignItems='left' margin='0'>
                          <Letter textAlign='left' margin='1rem 0 0 2rem '>
                            Legg til emne:
                          </Letter>
                          <InputSearchOval addCourseToSemester={addCourseToSemester} setInputValue={setInputValue} inputValue={inputValue} />
                          <FlexContainer alignItems='right'>
                            <Letter margin='0.5rem 1rem 0 0' textAlign='right'>
                              Snitt for semester:
                            </Letter>
                            <FlexContainer margin='0 1rem 0 0 '>
                              <HalfLeftOval>
                                <Letter fontSize='1.5rem' color='white'>
                                  {averageGrade}
                                </Letter>
                              </HalfLeftOval>
                              <HalfRightOval>
                                <Letter fontSize='1.5rem' color='white'>
                                  {letterGrade}
                                </Letter>
                              </HalfRightOval>
                            </FlexContainer>
                          </FlexContainer>
                        </FlexContainer>
                      </>
                    )}
                  </>
                </SemesterCoursesContainer>
              </>
            )}
          </SemesterContainer>
        </FlexContainer>
      )}
    </>
  );
};

export default React.memo(SemesterCalculator);
